import React, { FC, useState } from 'react';
import { useSkuLookUp, useXcmVendorSkuLookup } from '../../hooks/skuLookup';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { LineDetails } from '../../hooks/deal-details';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Pagination } from '@im/ui-lib/Pagination';
import CloseIcon from '@mui/icons-material/Close';

import {
  DataGrid,
  LoadingOverlay,
  NoRowsOverlay,
} from 'src/components/DataGrid';
import IconButton from '@mui/material/IconButton';

interface ImSkuTableProps {
  handleClose: () => void;
  isXcmVendor: boolean;
  item: LineDetails;
  vendorProgramId: string;
}

const ImSkuTable: FC<ImSkuTableProps> = ({
  handleClose,
  isXcmVendor,
  item,
  vendorProgramId,
}) => {
  const translate = useTranslate();
  const [pageSetup, setPageSetup] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 1, pageSize: 10 });
  const { data, isLoading } = useSkuLookUp({
    DealNumber: item.deal_number,
    DiscountDealId: item.discount_detail_id,
    IsXcmVendor: isXcmVendor,
    PageNumber: pageSetup.page,
    PageSize: pageSetup.pageSize,
    ...(item.product_group && { ProductGroup: item.product_group }),
    VendorProgramId: vendorProgramId,
    ...(item.vpn && { Vpn: item.vpn }),
    ...(item.crCcode && { crCcode: item.crCcode }),
    ...(item.offeringId && { OfferingId: item.offeringId }),
    ...(item.offeringType && { OfferingType: item.offeringType }),
    ...(item.productId && { ProductId: item.productId }),
    ...(item.sku && { sku: item.sku }),
  });

  const { data: xcmVendorSkuuData, isLoading: isXcmVendorSkuLoading } =
    useXcmVendorSkuLookup({
      DealNumber: item.deal_number,
      DiscountDealId: item.discount_detail_id,
      IsXcmVendor: isXcmVendor,
      PageNumber: pageSetup.page,
      PageSize: pageSetup.pageSize,
      ProductId: item.productId,
      VendorProgramId: Number(vendorProgramId),
      Version: '1',
      Vpn: item.vpn,
    });
  const columnNames = [
    {
      field: 'vpn',
      headerName: translate('VendorDeals.DiscountLineVpnName'),
    },
    {
      field: 'sku',
      headerName: translate('VendorDeals.DiscountLineIm_skuName'),
    },
    {
      field: 'crCcode',
      headerName: translate('VendorDeals.DiscountLineCrcCode'),
    },
    {
      field: 'skuClass',
      headerName: translate('VendorDeals.DiscountLineSku_class'),
    },
    {
      field: 'vendorCode',
      headerName: translate('VendorDeals.DiscountLineVendor_Code'),
    },
    {
      field: 'skuStatus',
      headerName: translate('VendorDeals.DiscountLineSku_Status'),
    },
    ...(isXcmVendor
      ? [
          {
            field: 'productId',
            headerName: translate('VendorDeals.DiscountLineProduct_ID'),
          },
          {
            field: 'offeringId',
            headerName: translate('VendorDeals.DiscountLineOffering_ID'),
          },
          {
            field: 'offeringType',
            headerName: translate('VendorDeals.DiscountLineOffering_Type'),
          },
        ]
      : []),
  ];
  const columns: GridColDef[] = columnNames.map((col) => ({
    align: 'left',
    field: col.field,
    flex: col.field === 'productId' ? undefined : 1,
    headerName: col.headerName,
    minWidth: col.field === 'productId' ? 180 : 120,
    renderCell: (item) => item.value,
    sortable: false,
  }));
  const requestPagination = () => (
    <Pagination
      data-testid="im-sku-look-up-table"
      onPageChange={(page) => setPageSetup({ ...pageSetup, page })}
      onRowsPerPageChange={(pageSize) => setPageSetup({ page: 1, pageSize })}
      page={pageSetup.page}
      rowsPerPage={pageSetup.pageSize}
      totalCount={
        isXcmVendor ? xcmVendorSkuuData?.totalCount ?? 0 : data?.totalCount ?? 0
      }
    />
  );
  const gridData = isXcmVendor
    ? xcmVendorSkuuData?.result ?? []
    : data?.result ?? [];

  return (
    <Dialog
      onClose={handleClose}
      open={true}
      sx={{
        '.MuiPaper-root': { minWidth: 'calc(100% - 500px)' },
      }}
    >
      <DialogTitle>
        {translate('VendorProgram.ImSkuLookUpTitle')}
        <IconButton
          data-testid="im-sku-look-up-close-icon"
          onClick={handleClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>
          <Stack>
            <Typography></Typography>
            <DataGrid
              autoHeight
              columnBuffer={columns.length}
              columns={columns}
              components={{
                LoadingOverlay: LoadingOverlay,
                NoRowsOverlay: NoRowsOverlay,
                Pagination: requestPagination,
              }}
              data-testid="im-sku-look-up-dataGrid"
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableSelectionOnClick
              getRowId={(row) =>
                isXcmVendor
                  ? `${row.productId}-${row.offeringId}-${row.crCcode}-${row.sku}`
                  : row.sku
              }
              hideFooterPagination={data ? data.result.length === 0 : true}
              loading={isXcmVendor ? isXcmVendorSkuLoading : isLoading}
              rows={gridData}
              sx={{
                '.MuiDataGrid-columnHeader--moving': {
                  backgroundColor: 'transparent',
                },
                '.MuiDataGrid-columnHeaderTitle': {
                  whiteSpace: 'normal',
                },
                '.MuiDataGrid-footerContainer>div': {
                  flexWrap: 'wrap',
                },
                '.MuiDataGrid-footerContainer>div>div': {
                  flex: 1,
                },
              }}
            />
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ImSkuTable;
